import {useFrame} from "@react-three/fiber";
import {GetRandomNumber, Range} from "js-vextensions";
import React, {useEffect, useMemo, useRef} from "react";
import * as THREE from "three";
import {RNG_SFC32} from "../../../../../Utils/General/RNG/PRNGs/RNG_SFC32.js";
import {Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {LHQInstance} from "../../LHQ/Scene.js";

export const WaterStreams = /*observer_mgl_r3*/((props: {lhq: LHQInstance})=>{
	const randomSeed = useMemo(()=>Math.random(), []);
	const prng = new RNG_SFC32({seed_float: randomSeed});

	return <>
		{Range(0, 6).map(i=>{
			const rand = (min: number, max: number)=>GetRandomNumber({min, max, mustBeInteger: false, randFunc: ()=>prng.GetNextFloat()});
			const startPos = new Vec3(rand(-5, 5), rand(-5, 5), rand(0, 5));
			const initialVelocity = new Vec3(rand(-1, 1), rand(-1, 1), rand(0.5, 1.5));
			return <WaterStream key={i} startPosition={startPos.To3JPos().toArray()} initialVelocity={initialVelocity.To3JPos().toArray()}/>;
		})}
	</>;
});

interface WaterStreamProps {
	startPosition: [number, number, number];
	initialVelocity: [number, number, number];
	particleCount?: number;
}

export function WaterStream({
	startPosition,
	initialVelocity,
	particleCount = 1000
}: WaterStreamProps) {
	const meshRef = useRef<THREE.InstancedMesh>(null);
	const particlesRef = useRef<Float32Array>(null);
	const velocitiesRef = useRef<Float32Array>(null);
	const agesRef = useRef<Float32Array>(null);

	// Initialize particle data
	const {positions, velocities, ages} = useMemo(()=>{
		const positions = new Float32Array(particleCount * 3);
		const velocities = new Float32Array(particleCount * 3);
		const ages = new Float32Array(particleCount);

		// Stagger initial spawn times so stream appears continuous
		for (let i = 0; i < particleCount; i++) {
			const t = i / particleCount;
			positions[i * 3] = startPosition[0];
			positions[i * 3 + 1] = startPosition[1];
			positions[i * 3 + 2] = startPosition[2];

			velocities[i * 3] = initialVelocity[0];
			velocities[i * 3 + 1] = initialVelocity[1];
			velocities[i * 3 + 2] = initialVelocity[2];

			ages[i] = t * 2; // 2 second lifespan
		}

		return {positions, velocities, ages};
	},
	// flat-map the startPosition and initialVelocity arrays, to ensure the block only gets recalculated when the *values* change
	[...startPosition, ...initialVelocity, particleCount]); // eslint-disable-line react-hooks/exhaustive-deps

	particlesRef.current = positions;
	velocitiesRef.current = velocities;
	agesRef.current = ages;

	// Create matrix for each instance
	const tempMatrix = useMemo(()=>new THREE.Matrix4(), []);

	// Initialize instance matrices
	useEffect(()=>{
		if (meshRef.current) {
			for (let i = 0; i < particleCount; i++) {
				tempMatrix.setPosition(startPosition[0], startPosition[1], startPosition[2]);
				tempMatrix.scale(new THREE.Vector3(0.02, 0.02, 0.02));
				meshRef.current.setMatrixAt(i, tempMatrix);
			}
			meshRef.current.instanceMatrix.needsUpdate = true;
		}
	}, [particleCount, startPosition, tempMatrix]);

	useFrame((state, delta_raw)=>{
		if (!particlesRef.current || !velocitiesRef.current || !agesRef.current || !meshRef.current) return;
		const delta = Math.min(delta_raw, 1 / 30); // Cap delta to avoid large time steps (eg. if user navigates away from page, we don't want gap in stream to form)

const gravity = -9.8; // z-up: gravity along -z

		for (let i = 0; i < particleCount; i++) {
			const i3 = i * 3;

			// Update age
			agesRef.current[i] += delta;

			let deltaForPhysics = delta;

			// Reset particle if too old
			if (agesRef.current[i] > 2) {
				agesRef.current[i] = 0;
				particlesRef.current[i3] = startPosition[0];
				particlesRef.current[i3 + 1] = startPosition[1];
				particlesRef.current[i3 + 2] = startPosition[2];
				velocitiesRef.current[i3] = initialVelocity[0];
				velocitiesRef.current[i3 + 1] = initialVelocity[1];
				velocitiesRef.current[i3 + 2] = initialVelocity[2];

				// add slight random offset to "progress" of new particle along route (to break up regularity, and fix that framerate may be too low otherwise and have air-gaps between new particles)
				deltaForPhysics = (Math.random() - 0.5) * delta;
			}

			(()=>{
				// z-up: gravity applies to the z-component (index i3 + 2)
				velocitiesRef.current[i3 + 2] += gravity * deltaForPhysics;

				// Add turbulence
				const turbulence = 1;
				velocitiesRef.current[i3] += (Math.random() - 0.5) * turbulence * deltaForPhysics;
				velocitiesRef.current[i3 + 2] += (Math.random() - 0.5) * turbulence * deltaForPhysics;

				particlesRef.current[i3] += velocitiesRef.current[i3] * deltaForPhysics;
				particlesRef.current[i3 + 1] += velocitiesRef.current[i3 + 1] * deltaForPhysics;
				particlesRef.current[i3 + 2] += velocitiesRef.current[i3 + 2] * deltaForPhysics;
			})();

			// Update instance matrix
			const scale = 0.02 + Math.random() * 0.02;
			tempMatrix.makeScale(scale, scale, scale);
			tempMatrix.setPosition(
				particlesRef.current[i3],
				particlesRef.current[i3 + 1],
				particlesRef.current[i3 + 2]
			);

			meshRef.current.setMatrixAt(i, tempMatrix);
		}

		meshRef.current.instanceMatrix.needsUpdate = true;
		meshRef.current.frustumCulled = false;
	});

	return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, particleCount]}>
      <sphereGeometry args={[1, 8, 6]} />
      <meshBasicMaterial
        color="#4A90E2"
        transparent
        opacity={0.7}
        side={THREE.DoubleSide}
      />
    </instancedMesh>
	);
}